// Single source of truth for the subscription tier policy (frontend side).
//
// MIRROR: kept in lockstep with three siblings —
//   backend/api-go/internal/platform/tiers/tiers.go   (Go)
//   backend/shared/tiers.py                            (Python)
//   src/api/schemas.ts                                 (Zod TierSchema enum)
// A change to any allowance / feature flag / preview cap / window / source /
// commercial-license flag, or to the shared fair-use caps, MUST be made in all
// of them. The Go parity tests (internal/platform/tiers/tiers_test.go:
// TestTierParity + TestTierSchemaZodParity) fail when they drift.

// Shared, tier-independent fair-use caps — bind every user, incl. "unlimited".
export const DAILY_GEN_CAP = 100
export const YEARLY_GEN_CAP = 5000
// Per-billing-cycle hard cap for "unlimited" cycle-window tiers (~5000/yr
// equivalent). The cycle sibling of YEARLY_GEN_CAP. See A4.
export const CYCLE_FAIR_USE_CAP = 450

export type Tier =
  | 'trial'
  | 'beta'
  | 'starter'
  | 'premium'
  | 'pro'
  | 'commercial'
  | 'sub_basic'
  | 'sub_pro'
  | 'sub_commercial'
  | 'none'
export type Window = 'year' | 'cycle'
export type PlanSource = 'lifetime' | 'stripe' | 'none'

export interface TierPolicy {
  /** null == unlimited (no plan ceiling; still bound by YEARLY_GEN_CAP /
   * CYCLE_FAIR_USE_CAP). Name kept for mirror/parity stability; for cycle-window
   * tiers this is the per-cycle allowance. */
  yearlyAllowance: number | null
  satelliteColor: boolean
  customBase: boolean
  /** Gates downloading generated models/bases. A tier may generate models
   * without being entitled to download them (e.g. beta). */
  download: boolean
  previewDailyCap: number
  window: Window
  commercialLicense: boolean
  source: PlanSource
}

// Canonical tier table (privilege-ascending). Record<Tier,...> makes a missing
// tier a compile error if Tier ever changes.
//
// `trial` is the open (codeless) self-serve front door and its policy is a
// deliberate COPY of `beta`'s. `beta` stays as the separate invite-program tier
// so existing beta users need no migration.
export const TIERS: Record<Tier, TierPolicy> = {
  trial: { yearlyAllowance: 5, satelliteColor: true, customBase: true, download: false, previewDailyCap: 60, window: 'year', commercialLicense: false, source: 'lifetime' },
  beta: { yearlyAllowance: 5, satelliteColor: true, customBase: true, download: false, previewDailyCap: 60, window: 'year', commercialLicense: false, source: 'lifetime' },
  starter: { yearlyAllowance: 4, satelliteColor: false, customBase: false, download: true, previewDailyCap: 60, window: 'year', commercialLicense: false, source: 'lifetime' },
  premium: { yearlyAllowance: 15, satelliteColor: true, customBase: true, download: true, previewDailyCap: 200, window: 'year', commercialLicense: false, source: 'lifetime' },
  pro: { yearlyAllowance: null, satelliteColor: true, customBase: true, download: true, previewDailyCap: 1000, window: 'year', commercialLicense: false, source: 'lifetime' },
  commercial: {
    yearlyAllowance: null,
    satelliteColor: true,
    customBase: true,
    download: true,
    previewDailyCap: 2000,
    window: 'year',
    commercialLicense: true,
    source: 'lifetime',
  },
  // --- Stripe subscription tiers (plan Decision 1); granted ONLY by
  // syncSubscription, never by codes. Paid subscribers may download. ---
  sub_basic: { yearlyAllowance: 5, satelliteColor: false, customBase: false, download: true, previewDailyCap: 60, window: 'cycle', commercialLicense: false, source: 'stripe' },
  sub_pro: { yearlyAllowance: null, satelliteColor: true, customBase: true, download: true, previewDailyCap: 1000, window: 'cycle', commercialLicense: false, source: 'stripe' },
  sub_commercial: {
    yearlyAllowance: null,
    satelliteColor: true,
    customBase: true,
    download: true,
    previewDailyCap: 2000,
    window: 'cycle',
    commercialLicense: true,
    source: 'stripe',
  },
  // none: explicitly set only (lapsed subscribers / code-less signups) — unknown
  // tiers still fall back to starter. Allowance 0 (not null!): zero generations,
  // ever. No download. Window is year purely so every tier has a valid window;
  // with a 0 cap the bucket choice can never matter.
  none: { yearlyAllowance: 0, satelliteColor: false, customBase: false, download: false, previewDailyCap: 20, window: 'year', commercialLicense: false, source: 'none' },
}

export function tierExists(name: string): name is Tier {
  return name in TIERS
}

/** Policy for name; falls back to the starter policy (deny-by-default). */
export function tierFor(name: string): TierPolicy {
  return tierExists(name) ? TIERS[name] : TIERS.starter
}

/** min(plan allowance, YEARLY_GEN_CAP); null allowance -> YEARLY_GEN_CAP. */
export function effectiveYearlyCap(name: string): number {
  const a = tierFor(name).yearlyAllowance
  return a === null ? YEARLY_GEN_CAP : Math.min(a, YEARLY_GEN_CAP)
}

/** min(plan allowance, CYCLE_FAIR_USE_CAP); null allowance -> CYCLE_FAIR_USE_CAP.
 * The cycle sibling of effectiveYearlyCap (A4). */
export function effectiveCycleCap(name: string): number {
  const a = tierFor(name).yearlyAllowance
  return a === null ? CYCLE_FAIR_USE_CAP : Math.min(a, CYCLE_FAIR_USE_CAP)
}

// Feature keys (must match the Go FeatureSatellite / FeatureCustomBase strings
// and the backend tier_upgrade_required "feature" extra). C2's UI binds to these.
export type Feature = 'satellite' | 'custom_base' | 'download' | 'commercial_license'

/** Whether tier grants feature; unknown tier/feature -> false (fail-closed). */
export function tierAllows(name: string, feature: Feature): boolean {
  const p = tierFor(name)
  switch (feature) {
    case 'satellite':
      return p.satelliteColor
    case 'custom_base':
      return p.customBase
    case 'download':
      return p.download
    case 'commercial_license':
      return p.commercialLicense
    default:
      return false
  }
}

// Human-readable tier labels (used by C1/C2 for display). Capitalized names.
const TIER_LABELS: Record<Tier, string> = {
  trial: 'Free Trial',
  beta: 'Beta',
  starter: 'Starter',
  premium: 'Premium',
  pro: 'Pro',
  commercial: 'Commercial',
  sub_basic: 'Basic',
  sub_pro: 'Pro',
  sub_commercial: 'Commercial',
  none: 'Free',
}

/** Display label for a tier; unknown name -> the starter label (fail-closed). */
export function tierLabel(name: string): string {
  return tierExists(name) ? TIER_LABELS[name] : TIER_LABELS.starter
}

/** A tier's plan allowance normalized to a yearly-equivalent count, for the
 * cross-window capacity comparison in lifetimeCovers. null (unlimited) → Infinity;
 * a cycle-window allowance is scaled ×12 (monthly billing). Deliberately NOT
 * clamped by the shared fair-use caps — this compares the plans' promised volume,
 * not the enforced ceiling. */
function yearlyEquivAllowance(p: TierPolicy): number {
  if (p.yearlyAllowance === null) return Infinity
  return p.window === 'cycle' ? p.yearlyAllowance * 12 : p.yearlyAllowance
}

/** MIRROR of the Go lifetimeCovers predicate
 * (backend/api-go/internal/api/billing/checkout.go, B4/D6): a lifetime tier
 * "covers" a requested sub tier when it grants every feature the sub tier grants
 * AND already gives at least as much generation volume (yearly-equivalent, so a
 * monthly cycle allowance is scaled ×12). So an unlimited lifetime tier
 * (pro/commercial) covers any sub tier, but a LIMITED lifetime tier covers a sub
 * tier only when its yearly-equivalent allowance is >= the sub tier's — premium's
 * 15/yr does NOT cover sub_basic's ~60/yr (5/cycle), so a limited backer may
 * subscribe to sub_basic purely for the extra volume (D6). The backend enforces
 * this as the 409 tier_already_covered refusal; this mirror only HIDES the covered
 * cell client-side (C2's pricing page). Change the two together — both sides pin
 * the same matrix in unit tests (Go: billing/covers_test.go, TS: tiers.test.ts).
 * NOT part of the four-mirror parity surface (the Go parity test scans tier
 * rows/consts, not functions). */
export function lifetimeCovers(cur: TierPolicy, req: TierPolicy): boolean {
  const featOK =
    (!req.satelliteColor || cur.satelliteColor) &&
    (!req.customBase || cur.customBase) &&
    (!req.commercialLicense || cur.commercialLicense)
  if (!featOK) return false
  return yearlyEquivAllowance(cur) >= yearlyEquivAllowance(req)
}
